import React, { useState } from "react";
import styled from "styled-components";
import { Input } from "@vds/inputs";
import { Icon } from "@vds/icons";
import GrayBoxes from "../../assets/gray-boxes.svg";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { IconButton } from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import { deleteMedia, imageUploadMedia } from "../../model/mediaService";
import { useSelector } from "react-redux";
import styles from "./components/basicForm.module.css";
import { validateNameField } from "./sitesUtil";
import { NAMING_VALIDATION_ERROR } from "../../global/constants/errorConstants";
import { device } from "../../styles/breakpoints";

const Wrapper = styled.div`
  /* Mobile-first: horizontal card layout with flexible height */
  display: flex;
  align-items: stretch;
  margin-bottom: 16px;
  width: 100%;
  min-height: auto;

  @media ${device.tablet} {
    margin-bottom: 20px;
    min-height: 80px;
  }
`;

const FrameForRectangles = styled.div`
  /* Mobile-first: stretches to fill parent height */
  display: flex;
  padding: 4px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 3px;
  align-self: stretch;
  border-radius: 4px 0px 0px 4px;
  border: 1px solid var(--gray-44, #6f7171);
  background: var(--gray-85, #d8dada) url(${GrayBoxes}) no-repeat center;
  background-size: calc(100% - 6px);
  width: 21px;
  flex-shrink: 0;

  @media ${device.tablet} {
    height: 80px;
    min-height: 80px;
  }
`;

const FrameForImageAndFPName = styled.div`
  /* Mobile-first: vertical layout, clean stacking */
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 12px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 4px;

  @media ${device.tablet} {
    flex-direction: row;
    align-items: center;
    padding: 8px 15px;
    gap: 16px;
    height: 80px;
  }
`;

const FrameForImageUploaded = styled.div`
  /* Mobile-first: horizontal row with icon and text */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  @media ${device.tablet} {
    width: auto;
    flex: 0 0 auto;
    min-width: 140px;
    max-width: 180px;
    justify-content: flex-start;
  }

  @media ${device.desktop} {
    min-width: 160px;
    max-width: 200px;
  }
`;

const FrameForUploadedFPNameRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: auto;
`;

const FrameForUploadedFPName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

const FrameForFPName = styled.div`
  /* Mobile-first: full width column layout */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1X, 4px);
  width: 100%;

  @media ${device.tablet} {
    width: auto;
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const FloorPlanNameField = styled.p`
  color: var(--elements-primary, #000);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Verizon NHG eTX";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
  width: 100%;
  height: auto;
`;

const UploadedImageName = styled.p`
  /* Mobile-first: readable file name */
  display: block;
  color: #000;
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Verizon-NHG-eDS";
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.5px;
  flex: 1;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${device.tablet} {
    font-size: 16px;
  }
`;

export const StyledInputDiv = styled.div`
  /* Mobile: full width, Tablet+: constrained */
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  min-height: 64px;
  gap: 40px;

  @media ${device.tablet} {
    min-height: auto;
  }
`;

const ActionButtonsContainer = styled.div`
  /* Mobile-first: full width row of action buttons */
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media ${device.tablet} {
    width: auto;
    flex: 0 0 auto;
  }
`;

function ImageUploadProgressTile({
  file,
  index,
  removeFile,
  sizeErrorFunction,
  fileTypes,
  maxSize,
  handleFileChange,
  onValidationChange,
}) {
  const { setFieldValue, values } = useFormikContext();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [floorNameError, setFloorNameError] = useState("");

  const handleFloorNameChange = (e) => {
    const rawValue = e.target.value;
    const floorName = rawValue?.split(".")[0];
    setFieldValue(`files.${index}.floorName`, floorName);
    setFieldValue(`sites.${index}.floorName`, floorName);

    // Validate format - check floorName (without extension) for invalid characters
    if (floorName && !validateNameField(floorName)) {
      setFloorNameError(NAMING_VALIDATION_ERROR);
      onValidationChange?.(true);
    } else {
      setFloorNameError("");
      onValidationChange?.(false);
    }
  };

  const uploadedFileName = file?.name || "No file uploaded";

  const handleDeleteClick = async () => {
    try {
      const response = await deleteMedia(
        values.files[index].imageid,
        activeSession
      );
      if (response.status === 200) {
        removeFile(index);
        setFieldValue(
          `sites`,
          values.sites.filter((s, i) => i !== index)
        );
      }
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  return (
    <Wrapper>
      <FrameForRectangles />
      <FrameForImageAndFPName>
        <FrameForImageUploaded>
          <FrameForUploadedFPName>
            <FrameForUploadedFPNameRow>
              <UploadedImageName>{uploadedFileName}</UploadedImageName>
              <Icon name="alternate-checkmark" size="large" color="#000000" />
            </FrameForUploadedFPNameRow>
          </FrameForUploadedFPName>
        </FrameForImageUploaded>
        <FrameForFPName>
          <StyledInputDiv>
            <Field name={`files.${index}.floorName`}>
              {({ field }) => (
                <>
                  <Input
                    {...field}
                    type="text"
                    label="Floor plan name"
                    data-testid="imageUploadProgressTile_floorplanNameInput"
                    onChange={handleFloorNameChange}
                    readOnly={false}
                    required={true}
                    disabled={false}
                    error={!!floorNameError}
                    errorText={floorNameError || " "}
                    value={
                      values?.files?.[index]?.floorName.indexOf(".") > -1
                        ? values.files[index].floorName.split(".")[0]
                        : values.files[index].floorName
                    }
                  />
                  <ErrorMessage
                    name={`files.${index}.floorName`}
                    component={"div"}
                    className={styles.errorLabel}
                    style={{ marginLeft: "10px" }}
                  />
                </>
              )}
            </Field>
          </StyledInputDiv>
        </FrameForFPName>
        <ActionButtonsContainer>
          <FileUploader
            multiple={false}
            handleChange={(newFile) => handleFileChange(newFile, index)}
            name="files"
            onSizeError={sizeErrorFunction}
            types={fileTypes}
            maxSize={maxSize}
            fileOrFiles={values.files}
          >
            <IconButton>
              <Icon name="edit" size="large" color="#000000" />
            </IconButton>
          </FileUploader>
          <IconButton
            data-testid="imageUploadProgressTile_trashIconDeleteFloorplan"
            onClick={handleDeleteClick}
          >
            <Icon name="trash" size="large" color="#000000" />
          </IconButton>
        </ActionButtonsContainer>
      </FrameForImageAndFPName>
    </Wrapper>
  );
}

export default ImageUploadProgressTile;
